import Loader from "components/shared/loader";
import Table from "components/shared/table";
import WithoutDriver from "components/shared/withoutDriver";
import { dateTimeToView } from "helpers/date";
import { DateColumnFilter, DebouncedDefaultColumnFilter } from "helpers/reactTable";
import {
    getServiceWorkAddressText,
    getServiceWorkNumber,
    getServiceWorkResponsible,
    getServiceWorkStatusLabel,
    getServiceWorkType,
    serviceWorkHasActiveStatus,
} from "helpers/serviceWorks";
import { matchSorter } from "match-sorter";
import moment from "moment";
import React, { useMemo, useState } from "react";

const tableDefaultProps = {
    minRows: 10,
    noDataText: "Нет данных для отображения",
    loadingComponent: <Loader />,
    pageSizeOptions: [30, 50, 100],
};

const normalizeServiceWork = (item) => ({
    source: item,
    status: getServiceWorkStatusLabel(item),
    type: getServiceWorkType(item),
    number: getServiceWorkNumber(item),
    responsible: getServiceWorkResponsible(item),
    createdAt: item.createdAt ? dateTimeToView(item.createdAt, "DD.MM.YYYY HH:mm (Z)") : "",
    createdAtRaw: item.createdAt,
    startDate: item.startDate ? dateTimeToView(item.startDate, "DD.MM.YYYY HH:mm (Z)") : "",
    startDateRaw: item.startDate,
    endDate: item.endDate ? dateTimeToView(item.endDate, "DD.MM.YYYY HH:mm (Z)") : "",
    endDateRaw: item.endDate,
    serviceTime: item.serviceTime ?? "",
    address: getServiceWorkAddressText(item),
});

const filterItems = (items, filtered) =>
    Object.values(filtered).reduce((memo, { id, value, filterType }) => {
        if (!value) {
            return memo;
        }

        if (filterType === "date") {
            if (!value.value) {
                return memo;
            }

            const dateKey = `${id}Raw`;
            return memo.filter((item) => {
                if (!item[dateKey]) {
                    return false;
                }

                const itemDate = moment(item[dateKey]);
                const filterDate = moment(value.value);

                switch (value.key) {
                    case "ge":
                        return itemDate.isSameOrAfter(filterDate);
                    case "le":
                        return itemDate.isSameOrBefore(filterDate.endOf("minute"));
                    case "eq":
                    default:
                        return itemDate.isBetween(
                            filterDate.clone().startOf("minute"),
                            filterDate.clone().endOf("minute"),
                            null,
                            "[]",
                        );
                }
            });
        }

        return memo.filter(
            (item) => matchSorter([item[id]], value.trim(), { threshold: matchSorter.rankings.CONTAINS }).length !== 0,
        );
    }, items);

const ServiceWorksPlanning = ({ serviceWorks = [], showAll = false }) => {
    const [filters, setFilters] = useState({});
    const items = useMemo(
        () => (showAll ? serviceWorks : serviceWorks.filter(serviceWorkHasActiveStatus)),
        [serviceWorks, showAll],
    );
    const rows = useMemo(() => items.map(normalizeServiceWork), [items]);
    const filteredRows = useMemo(() => filterItems(rows, filters), [filters, rows]);
    const columns = useMemo(
        () => [
            {
                Header: "Статус СР",
                accessor: "status",
                filterable: true,
                width: 160,
            },
            {
                Header: "Тип СР",
                accessor: "type",
                filterable: true,
                width: 190,
            },
            {
                Header: "Ответственный за СР",
                accessor: "responsible",
                filterable: true,
                width: 180,
            },
            {
                Header: "Дата постановки",
                accessor: "createdAt",
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                width: 170,
            },
            {
                Header: "Начало работ",
                accessor: "startDate",
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                width: 170,
            },
            {
                Header: "Окончание работ",
                accessor: "endDate",
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                width: 170,
            },
            {
                Header: "Время выполнения СР",
                accessor: "serviceTime",
                width: 170,
            },
            {
                Header: "Адрес СТО",
                accessor: "address",
                width: 260,
                Cell: ({ value }) => <WithoutDriver title={value} />,
            },
        ],
        [],
    );

    const onFilteredChange = ({ id, ...rest }) => {
        setFilters((prev) => ({ ...prev, [id]: { id, ...rest } }));
    };

    return (
        <Table
            {...tableDefaultProps}
            columns={columns}
            data={filteredRows}
            defaultColumn={{
                sortable: true,
                Filter: DebouncedDefaultColumnFilter,
            }}
            filtered={filters}
            loading={false}
            onFilteredChange={onFilteredChange}
        />
    );
};

export default ServiceWorksPlanning;
